import { Route } from '../types';

export interface RouteGroup {
  area: string;
  crag: string;
  displayName: string;
  routes: Route[];
}

// Parse location string to extract area and crag
export function parseLocation(location: string): { area: string; crag: string; displayName: string } {
  // Split by " > " and take everything up to "Red River Gorge"
  const parts = location.split(' > ');
  
  // Find the index of "Red River Gorge" or take all parts except the last one (Kentucky)
  const rrGorgeIndex = parts.findIndex(part => part.includes('Red River Gorge'));
  const relevantParts = rrGorgeIndex !== -1 
    ? parts.slice(0, rrGorgeIndex)
    : parts.slice(0, -1); // Remove Kentucky
  
  if (relevantParts.length === 0) {
    return { area: 'Unknown', crag: 'Unknown', displayName: 'Unknown Area' };
  }
  
  if (relevantParts.length === 1) {
    return { area: relevantParts[0], crag: relevantParts[0], displayName: relevantParts[0] };
  }
  
  // Reverse the order of relevant parts
  const reversedParts = [...relevantParts].reverse();
  
  // First part (originally last) is the area, second part (originally second-to-last) is the crag
  const area = reversedParts[0];
  const crag = reversedParts[1] || reversedParts[0];
  
  // Create display name: "Area > Crag"
  const displayName = area === crag ? crag : `${area} > ${crag}`;
  
  return { area, crag, displayName };
}

// Group routes by area and crag
export function groupRoutesByLocation(routes: Route[]): RouteGroup[] {
  const groupsMap = new Map<string, RouteGroup>();
  
  routes.forEach(route => {
    const { area, crag, displayName } = parseLocation(route.Location);
    const groupKey = `${area}|${crag}`;
    
    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, {
        area,
        crag,
        displayName,
        routes: []
      });
    }
    
    groupsMap.get(groupKey)!.routes.push(route);
  });
  
  // Convert to array and sort by area, then by crag
  return Array.from(groupsMap.values()).sort((a, b) => {
    const areaCompare = a.area.localeCompare(b.area);
    if (areaCompare !== 0) return areaCompare;
    return a.crag.localeCompare(b.crag);
  });
}

// Get a shorter display name for route cards (just the crag)
export function getRouteCardLocation(route: Route): string {
  const { crag } = parseLocation(route.Location);
  return crag;
}
